import { useMutation } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle2,
  CircleAlert,
  RefreshCw,
} from 'lucide-react'
import { useId, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { Button } from '../../ui/Button/Button'
import { StatusBadge } from '../../ui/StatusBadge/StatusBadge'
import { useAuth } from '../auth/AuthProvider'
import panel from '../settings/SettingsSection.module.css'
import { CommunicationsSetupStatus } from './CommunicationsSetupStatus'
import { ExistingClinicAdoption } from './ExistingClinicAdoption'
import {
  getOnboardingApiClient,
  OnboardingApiError,
  type OnboardingApiClient,
} from './onboarding-api'
import type {
  ConfirmedOrganizationClinic,
  ResolvedNexHealthLocation,
} from './organization-onboarding-contracts'
import styles from './OrganizationClinicOnboardingPage.module.css'
import { useRefreshClinicsOnLeave } from './use-refresh-clinics-on-leave'

const ORGANIZATION = '/settings?section=organization'

type Problem = {
  /** Where the message belongs: on the ID itself, or about the attempt. */
  on: 'field' | 'attempt'
  title?: string
  message: string
  retry?: boolean
}

function problem(error: unknown): Problem {
  const code = error instanceof OnboardingApiError ? error.code : null
  if (code === 'nexhealth-location-invalid')
    return {
      on: 'field',
      message: 'That isn’t a valid NexHealth Location ID.',
    }
  if (code === 'nexhealth-location-inaccessible')
    return {
      on: 'field',
      message:
        'That Location ID does not exist or is not accessible through Flio’s current NexHealth account.',
    }
  if (code === 'nexhealth-location-sync-unhealthy')
    return {
      on: 'attempt',
      title: 'NexHealth sync isn’t active for this location',
      message:
        'The NexHealth/PMS connection became unhealthy. Verify the location again after NexHealth read and write access are restored.',
      retry: true,
    }
  if (code === 'organization-conflict')
    return {
      on: 'attempt',
      title: 'This clinic can’t be added right now',
      message:
        'That clinic is already connected, or another clinic setup changed. Refresh and try again.',
    }
  if (code === 'forbidden')
    return {
      on: 'attempt',
      title: 'Not allowed',
      message: 'Only organization Owners and Administrators can add clinics.',
    }
  if (code === 'nexhealth-location-unavailable' || code === 'transport')
    return {
      on: 'attempt',
      title: 'NexHealth couldn’t be reached',
      message: 'Nothing was created. Try again in a moment.',
      retry: true,
    }
  return {
    on: 'attempt',
    title: 'Something went wrong',
    message: 'Flio could not verify that NexHealth location. Try again later.',
    retry: true,
  }
}

function healthLabel(status: 'GREEN' | 'RED' | 'UNKNOWN') {
  return status === 'GREEN'
    ? 'Healthy'
    : status === 'RED'
      ? 'Degraded'
      : 'Unknown'
}

const STEPS = ['Location ID', 'Review', 'Done'] as const

/**
 * Add a clinic from one exact NexHealth Location ID: verify it, review what
 * NexHealth returned, then confirm. Nothing is created until Add clinic, and
 * the server verifies the same location again before it creates anything.
 */
export function OrganizationClinicOnboardingPage({
  apiClient,
}: {
  apiClient?: OnboardingApiClient
} = {}) {
  const auth = useAuth()
  const navigate = useNavigate()
  const user = auth.status === 'authenticated' ? auth.user : null
  const fieldId = useId()
  const hintId = useId()
  const [createdClinic, setCreatedClinic] =
    useState<ConfirmedOrganizationClinic | null>(null)
  const [locationId, setLocationId] = useState('')
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [resolved, setResolved] = useState<ResolvedNexHealthLocation | null>(
    null
  )
  const client = apiClient ?? getOnboardingApiClient()
  const syncDegraded = resolved ? resolved.syncHealth !== 'ACTIVE' : false

  const resolveLocation = useMutation({
    mutationFn: (id: number) =>
      client.resolveOrganizationClinicLocation!(user!, id),
    onSuccess: setResolved,
  })
  // A clinic added or linked here joins the organization answer and the
  // clinic list (the latest one selected) when this page closes, so its
  // success view stays.
  const newClinic = useRefreshClinicsOnLeave()
  const confirmLocation = useMutation({
    mutationFn: () =>
      client.confirmOrganizationClinic!(
        user!,
        resolved!.locationId,
        resolved!.verificationFingerprint
      ),
    onSuccess: (clinic) => {
      setCreatedClinic(clinic)
      newClinic.added(clinic.tenantUid)
    },
  })
  function leave(to: string) {
    newClinic.leave()
    void navigate(to)
  }

  const step = createdClinic ? 2 : resolved ? 1 : 0
  const resolveProblem = resolveLocation.isError
    ? problem(resolveLocation.error)
    : null
  const confirmProblem = confirmLocation.isError
    ? problem(confirmLocation.error)
    : null
  const shownFieldError =
    fieldError ??
    (resolveProblem?.on === 'field' ? resolveProblem.message : null)

  function verify(event?: FormEvent) {
    event?.preventDefault()
    const value = locationId.trim()
    setResolved(null)
    confirmLocation.reset()
    if (!value) {
      setFieldError('Enter the clinic’s NexHealth Location ID.')
      return
    }
    const numeric = Number(value)
    if (
      !/^[0-9]+$/.test(value) ||
      !Number.isSafeInteger(numeric) ||
      numeric < 1
    ) {
      setFieldError('Use digits only, for example 300123.')
      return
    }
    setFieldError(null)
    resolveLocation.mutate(numeric)
  }

  return (
    <div className={styles.page}>
      <Link to={ORGANIZATION} className={styles.back}>
        <ArrowLeft size={15} aria-hidden="true" /> Organization
      </Link>

      <section className={panel.panel} aria-labelledby={`${fieldId}-title`}>
        <div className={panel.panelHeader}>
          <div className={panel.heading}>
            <span className={panel.eyebrow}>Add clinic</span>
            <h2 id={`${fieldId}-title`}>
              {createdClinic ? 'Clinic added' : 'Connect a NexHealth clinic'}
            </h2>
            <p>
              {createdClinic
                ? 'The clinic workspace is ready in your organization.'
                : 'Flio verifies the exact location with your organization’s NexHealth connection before any clinic workspace is created.'}
            </p>
          </div>
        </div>

        <ol className={styles.steps} aria-label="Add clinic steps">
          {STEPS.map((label, index) => (
            <li
              key={label}
              data-state={
                index < step ? 'done' : index === step ? 'current' : 'next'
              }
              aria-current={index === step ? 'step' : undefined}
            >
              <span className={styles.stepMarker} aria-hidden="true">
                {index < step ? <Check size={13} /> : index + 1}
              </span>
              {label}
            </li>
          ))}
        </ol>

        {createdClinic ? (
          <div className={styles.body}>
            <div className={styles.created}>
              <CheckCircle2
                size={22}
                aria-hidden="true"
                className={styles.createdIcon}
              />
              <div>
                <strong>{createdClinic.name}</strong>
                <p>Patient and schedule data keep syncing in the background.</p>
              </div>
            </div>
            <CommunicationsSetupStatus
              tenantUid={createdClinic.tenantUid}
              fallbackStatus={createdClinic.communicationsStatus}
              apiClient={client}
              actionLabel="Set up phone & texting"
              primary
              render={({ ready, details }) => (
                <>
                  <div className={styles.nextStep}>
                    <strong>
                      {ready
                        ? 'Phone & texting is active.'
                        : 'Next: set up phone & texting'}
                    </strong>
                    {ready ? null : (
                      <p>
                        So Flio can answer this clinic’s calls and texts. You
                        can also do this later from Organization.
                      </p>
                    )}
                    {details}
                  </div>
                  <div className={styles.footer}>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => leave(ORGANIZATION)}
                    >
                      Back to Organization
                    </Button>
                    {ready ? null : (
                      <Button
                        type="button"
                        onClick={() =>
                          leave(
                            `/organization/clinics/${encodeURIComponent(createdClinic.tenantUid)}/communications`
                          )
                        }
                      >
                        Set up phone &amp; texting
                      </Button>
                    )}
                  </div>
                </>
              )}
            />
          </div>
        ) : resolved ? (
          <div className={styles.body}>
            <article
              className={styles.location}
              aria-label="Resolved NexHealth clinic"
            >
              <div>
                <h3>{resolved.locationName}</h3>
                <p>{resolved.address ?? 'No address returned by NexHealth'}</p>
              </div>
              <ul className={styles.checks} aria-label="Verification">
                <li>
                  <Check size={14} aria-hidden="true" /> Location verified
                </li>
                <li>
                  <Check size={14} aria-hidden="true" /> Clinic data readable
                </li>
                <li data-warning={syncDegraded || undefined}>
                  {syncDegraded ? (
                    <CircleAlert size={14} aria-hidden="true" />
                  ) : (
                    <Check size={14} aria-hidden="true" />
                  )}
                  Booking writes {syncDegraded ? 'not ready' : 'ready'}
                </li>
              </ul>
              <dl className={styles.facts}>
                <div>
                  <dt>Location ID</dt>
                  <dd>{resolved.locationId}</dd>
                </div>
                <div>
                  <dt>Institution</dt>
                  <dd>{resolved.institutionName}</dd>
                </div>
                <div>
                  <dt>Subdomain</dt>
                  <dd>{resolved.subdomain}</dd>
                </div>
                <div>
                  <dt>Practice system</dt>
                  <dd>{resolved.pms}</dd>
                </div>
                <div>
                  <dt>PMS sync read health</dt>
                  <dd>
                    <StatusBadge
                      tone={
                        resolved.readStatus === 'GREEN' ? 'success' : 'warning'
                      }
                    >
                      {healthLabel(resolved.readStatus)}
                    </StatusBadge>
                  </dd>
                </div>
                <div>
                  <dt>PMS sync write health</dt>
                  <dd>
                    <StatusBadge
                      tone={
                        resolved.writeStatus === 'GREEN' ? 'success' : 'warning'
                      }
                    >
                      {healthLabel(resolved.writeStatus)}
                    </StatusBadge>
                  </dd>
                </div>
              </dl>
            </article>
            {syncDegraded ? (
              <div
                className={`${panel.notice} ${styles.notice}`}
                data-tone="warning"
                role="status"
              >
                <AlertTriangle size={16} aria-hidden="true" />
                <div>
                  <strong>PMS synchronization is degraded</strong>
                  <p>
                    Clinic data is readable and the workspace can be created.
                    Booking and AI scheduling writes remain restricted until PMS
                    write readiness is healthy.
                  </p>
                </div>
              </div>
            ) : null}
            {confirmProblem ? (
              <AttemptProblem problem={confirmProblem} />
            ) : null}
            <div className={styles.footer}>
              <Button
                type="button"
                variant="secondary"
                disabled={confirmLocation.isPending}
                onClick={() => {
                  setResolved(null)
                  confirmLocation.reset()
                }}
              >
                Use another ID
              </Button>
              <Button
                type="button"
                disabled={confirmLocation.isPending || !user}
                onClick={() => confirmLocation.mutate()}
              >
                {confirmLocation.isPending ? 'Adding clinic…' : 'Add clinic'}
              </Button>
            </div>
          </div>
        ) : (
          <form className={styles.body} onSubmit={verify} noValidate>
            <div className={styles.fieldGroup}>
              <label className={panel.field} htmlFor={fieldId}>
                <span>NexHealth Location ID</span>
                <input
                  id={fieldId}
                  className={styles.idInput}
                  inputMode="numeric"
                  autoComplete="off"
                  spellCheck={false}
                  value={locationId}
                  readOnly={resolveLocation.isPending}
                  aria-invalid={shownFieldError ? true : undefined}
                  aria-describedby={hintId}
                  onChange={(event) => {
                    setLocationId(event.target.value)
                    setFieldError(null)
                    if (resolveLocation.isError) resolveLocation.reset()
                  }}
                />
              </label>
              {shownFieldError ? (
                <p id={hintId} className={panel.fieldError} role="alert">
                  <AlertTriangle size={14} aria-hidden="true" />
                  {shownFieldError}
                </p>
              ) : (
                <p id={hintId} className={panel.hint}>
                  The numeric ID of the clinic’s location in NexHealth. Only
                  that exact location is checked; no list is shown.
                </p>
              )}
            </div>
            {resolveProblem && resolveProblem.on === 'attempt' ? (
              <AttemptProblem
                problem={resolveProblem}
                onRetry={() => verify()}
              />
            ) : null}
            <div className={styles.footer}>
              <Button
                type="button"
                variant="secondary"
                disabled={resolveLocation.isPending}
                onClick={() => void navigate(ORGANIZATION)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={resolveLocation.isPending || !user}
              >
                {resolveLocation.isPending ? 'Verifying…' : 'Verify location'}
              </Button>
            </div>
          </form>
        )}
      </section>

      {/* Kept mounted after step 1, so a linked clinic's result is still
          there on returning to it. */}
      <div hidden={step !== 0}>
        <ExistingClinicAdoption apiClient={client} onLinked={newClinic.added} />
      </div>
    </div>
  )
}

function AttemptProblem({
  problem,
  onRetry,
}: {
  problem: Problem
  onRetry?: () => void
}) {
  return (
    <div
      className={`${panel.notice} ${styles.notice}`}
      data-tone="warning"
      role="alert"
    >
      <AlertTriangle size={16} aria-hidden="true" />
      <div>
        {problem.title ? <strong>{problem.title}</strong> : null}
        <p>{problem.message}</p>
        {problem.retry && onRetry ? (
          <Button type="button" variant="secondary" onClick={onRetry}>
            <RefreshCw size={14} aria-hidden="true" /> Try again
          </Button>
        ) : null}
      </div>
    </div>
  )
}
